/**
 * AWAC i18n — French-default bilingual strings for the Lib Mont-Saint-Hilaire pilot.
 *
 * Quebec's Bill 96 requires French-first presentation, so the default language
 * is 'fr'. Resolution order:
 *   1. ?lang=en|fr query parameter (also persisted for the session)
 *   2. stored preference (localStorage 'awac_lang')
 *   3. default 'fr'
 *
 * Components subscribe through useAwacLang(); calling setLang() persists the
 * preference and re-renders every subscriber via the 'awac:lang-changed' event.
 */

import { useEffect, useState } from 'react';

const LANG_KEY = 'awac_lang';
const SUPPORTED = ['fr', 'en'];

const STRINGS = {
    fr: {
        // ----- Landing: nav -----
        'nav.pilot': 'Pilote',
        'nav.residences': 'Résidences',
        'nav.neighborhood': 'Quartier',
        'nav.chat': 'Clavarder avec Lia',
        // ----- Landing: hero -----
        'hero.eyebrow': 'Mont-Saint-Hilaire · Québec',
        'hero.titlePre': 'Vivre au pied de ',
        'hero.titleEm': 'la montagne',
        'hero.lede':
            'Lib est un nouveau projet de condominiums à Mont-Saint-Hilaire, entre la rivière Richelieu et la montagne — des résidences d\u2019une à trois chambres sur la Rive-Sud de Montréal.',
        'hero.chat': 'Clavarder avec Lia',
        'hero.viewResidences': 'Voir les résidences',
        // ----- Landing: residences -----
        'residences.title': 'Les résidences',
        'residences.lede':
            'Cinq plans, du pied-à-terre d\u2019une chambre à la résidence familiale de trois chambres. Demandez à Lia les plans, les photos et les disponibilités de chacun.',
        'plan.bed': 'ch.',
        'plan.bath': 'sdb',
        'plan.layout': 'configuration',
        'plan.sqft': 'pi\u00B2',
        'plan.interior': 'superficie intérieure',
        'plan.asking': 'prix demandé',
        'plan.ask': 'Demander à Lia →',
        'plan.askMessage': 'Parle-moi du {plan}',
        'plans.note': 'Les prix et disponibilités affichés sont des données pilotes et peuvent changer.',
        // ----- Landing: neighborhood -----
        'neighborhood.title': 'Le quartier',
        'neighborhood.p1':
            'Mont-Saint-Hilaire est situé sur la rive sud de la rivière Richelieu, au pied de la montagne qui donne son nom à la ville. Le secteur est reconnu pour sa réserve naturelle et ses vergers, avec la rivière, les commerces locaux et le train de banlieue vers le centre-ville de Montréal à proximité.',
        'neighborhood.p2':
            'Lia peut cartographier ce qui compte pour vous — écoles, épiceries, cafés, transports et sentiers autour du projet — directement dans la conversation.',
        'askList.title': 'Des questions pour Lia',
        'askList.1': 'Montre-moi les plans',
        'askList.2': 'Montre-moi les unités disponibles sous 420 000 $',
        'askList.3': 'Montre-moi le quartier',
        'askList.4': 'Réserver une visite avec l\u2019équipe des ventes',
        // ----- Landing: CTA -----
        'cta.title': 'Lia est sur la page.',
        'cta.lede':
            'Notre agente immobilière IA répond en quelques secondes — plans, unités, carte du quartier et réservation de visites, en français ou en anglais.',
        'cta.chip1': 'Montre-moi les plans',
        'cta.chip2': 'Montre-moi le quartier',
        'cta.chip3': 'Qu\u2019est-ce que je peux avoir pour 400 000 $?',
        'cta.start': 'Commencer la conversation',
        // ----- Landing: footer -----
        'footer.blurb':
            'Une expérience pilote par AI Real Estate. Les conversations sur cette page sont prises en charge par Lia, une agente IA construite sur Salesforce Agentforce.',
        'footer.fine':
            'Ceci est un environnement de test — les inscriptions, prix et disponibilités sont des données d\u2019exemple pour le pilote.',
        // ----- Proactive nudge -----
        'nudge.text': 'Des questions sur les plans ou les prix? Lia répond en quelques secondes.',
        'nudge.cta': 'Poser une question',
        'nudge.dismiss': 'Ignorer',
        'nudge.message': 'J\u2019ai des questions sur le projet Lib Mont-Saint-Hilaire.',
        // ----- Chat chrome -----
        'chat.subtitle': 'Agente immobilière IA',
        'chat.inputPlaceholder': 'Écrivez votre message…',
        'chat.bookedChip': 'Visite réservée',
        'chat.minimize': 'Réduire la fenêtre de clavardage',
        'chat.close': 'Fermer la conversation',
        'chat.closeWindow': 'Fermer la fenêtre',
        'chat.transcriptLabel': 'Messages de la conversation',
        'chat.scrollLatest': 'Aller au dernier message',
        'chat.suggestionsLabel': 'Demandez-moi par exemple :',
        'chat.suggestion1': 'Réserver une visite',
        'chat.suggestion2': 'Montre-moi le quartier',
        'chat.suggestionsDismiss': 'Masquer les suggestions',
        'chat.send': 'Envoyer le message',
        'chat.terms': 'En continuant, vous acceptez nos conditions. Cette conversation peut être enregistrée.',
        'chat.endTitle': 'Terminer la conversation?',
        'chat.endBody':
            'Votre conversation avec Lia sera fermée. Vous pourrez en démarrer une nouvelle à tout moment.',
        'chat.endConfirm': 'Terminer',
        'chat.endCancel': 'Annuler',
        // ----- Template error boundary -----
        'tpl.errorTitle': 'Ce contenu n\u2019a pas pu être affiché.',
        'tpl.errorBody': 'Vous pouvez continuer la conversation — Lia a toujours toutes les informations.',
        'tpl.retry': 'Réessayer',
        // ----- Chat transcript chrome -----
        'chat.started': 'Conversation commencée le {date} à {time}',
        'chat.ended': 'Conversation terminée le {date} à {time}',
        'chat.joined': '{name} a rejoint la conversation · {time}',
        'chat.left': '{name} a quitté la conversation · {time}',
        'chat.you': 'Vous',
        'chat.senderAt': '{name} à {time}',
        'chat.readAt': 'Lu à {time} • ',
        'chat.deliveredAt': 'Livré à {time} • ',
        'chat.sent': 'Envoyé • ',
        // ----- Gallery panel -----
        'gallery.close': 'Fermer la galerie',
        'gallery.details': 'Détails',
        'gallery.unknownTitle': 'Ce contenu ne peut pas être affiché ici.',
        'gallery.unknownSub': 'Composant non pris en charge',
        'gallery.FloorPlanShowcase': 'Plans d\u2019étage',
        'gallery.UnitCarousel': 'Unités disponibles',
        'gallery.NeighborhoodMap': 'Quartier',
        'gallery.BookingFrame': 'Réserver une visite',
        'gallery.BookingSlots': 'Réserver une visite',
        'gallery.BookingConfirm': 'Visite confirmée',
        'link.UnitCarousel': 'Voir les unités disponibles',
        'link.NeighborhoodMap': 'Voir le quartier',
        'link.FloorPlanShowcase': 'Voir les plans',
        'link.gallery': 'Voir la galerie',
        // ----- Neighborhood map template -----
        'map.title': 'À proximité',
        'map.subtitle': 'Points d\u2019intérêt autour de {name}',
        'map.cat.Groceries': 'Épiceries',
        'map.cat.Pharmacies': 'Pharmacies',
        'map.cat.Restaurants': 'Restaurants',
        'map.cat.Parks': 'Parcs',
        'map.cat.Schools': 'Écoles',
        'map.cat.Fitness': 'Gyms et entraînement',
        'map.cat.Transit': 'Transport en commun',
        'map.cat.Shopping': 'Magasinage',
        'map.cat.Other': 'Autre',
        'map.loading': 'Chargement de la carte…',
        'map.error': 'La carte n\u2019a pas pu se charger. Vérifiez votre connexion et réessayez.',
        'map.footnote': 'Données cartographiques © contributeurs OpenStreetMap. L\u2019emplacement des épingles est illustratif.',
        'map.projectLocation': 'Emplacement du projet',
        'map.openInMaps': 'Ouvrir dans Cartes ›',
        'map.filterLabel': 'Filtrer les points d\u2019intérêt par catégorie',
        'map.mapLabel': 'Carte du quartier',
        // ----- Unit carousel / floor plans templates -----
        'units.title': 'Unités correspondantes',
        'units.noPhotos': 'Aucune photo pour le moment',
        'units.empty': 'Aucune unité correspondante à afficher.',
        'units.book': 'Réserver une visite',
        'units.bed': 'ch.',
        'units.beds': 'ch.',
        'units.bath': 'sdb',
        'units.baths': 'sdb',
        'units.sqft': 'pi\u00B2',
        'units.sqftBalcony': 'pi\u00B2 balcon',
        'units.den': '+ den',
        'units.photosOf': 'Photos de {name}',
        'units.photoAlt': '{name} — photo {n} de {count}',
        'units.prevPhoto': 'Photo précédente',
        'units.nextPhoto': 'Photo suivante',
        'fp.title': 'Plans d\u2019étage',
        'fp.empty': 'Aucun plan d\u2019étage à afficher.',
        'fp.imageFallback': 'Plan d\u2019étage',
        'fp.from': 'À partir de'
    },
    en: {
        'nav.pilot': 'Pilot',
        'nav.residences': 'Residences',
        'nav.neighborhood': 'Neighborhood',
        'nav.chat': 'Chat with Lia',
        'hero.eyebrow': 'Mont-Saint-Hilaire · Québec',
        'hero.titlePre': 'Live at the foot of ',
        'hero.titleEm': 'the mountain',
        'hero.lede':
            'Lib is a new condominium project in Mont-Saint-Hilaire, between the Richelieu River and the mountain — one- to three-bedroom homes on Montréal\u2019s South Shore.',
        'hero.chat': 'Chat with Lia',
        'hero.viewResidences': 'View the residences',
        'residences.title': 'The residences',
        'residences.lede':
            'Five layouts, from a one-bedroom pied-à-terre to a three-bedroom family home. Ask Lia for the plans, photos and availability of any of them.',
        'plan.bed': 'bed',
        'plan.bath': 'bath',
        'plan.layout': 'layout',
        'plan.sqft': 'sq ft',
        'plan.interior': 'interior area',
        'plan.asking': 'asking price',
        'plan.ask': 'Ask Lia →',
        'plan.askMessage': 'Tell me about {plan}',
        'plans.note': 'Pricing and availability shown are pilot data and may change.',
        'neighborhood.title': 'The neighborhood',
        'neighborhood.p1':
            'Mont-Saint-Hilaire sits on the south shore of the Richelieu River, at the foot of the mountain that gives the town its name. The area is known for its nature reserve and orchards, with the river, local shops and commuter rail to downtown Montréal close by.',
        'neighborhood.p2':
            'Lia can map what matters to you — schools, groceries, cafés, transit and trails around the project — right inside the conversation.',
        'askList.title': 'Things to ask Lia',
        'askList.1': 'Show me the plans',
        'askList.2': 'Show me available homes under $420k',
        'askList.3': 'Show me the neighborhood',
        'askList.4': 'Book a visit with the sales team',
        'cta.title': 'Lia is on the page.',
        'cta.lede':
            'Our AI real estate agent answers in seconds — floor plans, homes, the neighborhood map and visit bookings, in English or in French.',
        'cta.chip1': 'Show me the plans',
        'cta.chip2': 'Show me the neighborhood',
        'cta.chip3': 'What can I get for $400k?',
        'cta.start': 'Start the conversation',
        'footer.blurb':
            'A pilot experience by AI Real Estate. Conversations on this page are handled by Lia, an AI agent built on Salesforce Agentforce.',
        'footer.fine':
            'This is a test environment — listings, pricing and availability are sample data for the pilot.',
        'nudge.text': 'Questions about the plans or pricing? Lia answers in seconds.',
        'nudge.cta': 'Ask a question',
        'nudge.dismiss': 'Dismiss',
        'nudge.message': 'I have questions about the Lib Mont-Saint-Hilaire project.',
        'chat.subtitle': 'AI Real Estate Agent',
        'chat.inputPlaceholder': 'Type your message…',
        'chat.bookedChip': 'Visit booked',
        'chat.minimize': 'Minimize chat window',
        'chat.close': 'Close the conversation',
        'chat.closeWindow': 'Close window',
        'chat.transcriptLabel': 'Conversation messages',
        'chat.scrollLatest': 'Scroll to latest message',
        'chat.suggestionsLabel': 'Ask me things like:',
        'chat.suggestion1': 'Book a visit',
        'chat.suggestion2': 'Show me the neighborhood',
        'chat.suggestionsDismiss': 'Dismiss suggestions',
        'chat.send': 'Send message',
        'chat.terms': 'By continuing, you agree to our terms. This conversation may be recorded.',
        'chat.endTitle': 'End the conversation?',
        'chat.endBody': 'Your conversation with Lia will be closed. You can start a new one anytime.',
        'chat.endConfirm': 'End conversation',
        'chat.endCancel': 'Cancel',
        'tpl.errorTitle': 'This content could not be displayed.',
        'tpl.errorBody': 'You can keep chatting — Lia still has all the information.',
        'tpl.retry': 'Retry',
        'chat.started': 'Conversation started: {date} at {time}',
        'chat.ended': 'Conversation ended: {date} at {time}',
        'chat.joined': '{name} joined the conversation · {time}',
        'chat.left': '{name} left the conversation · {time}',
        'chat.you': 'You',
        'chat.senderAt': '{name} at {time}',
        'chat.readAt': 'Read at {time} • ',
        'chat.deliveredAt': 'Delivered at {time} • ',
        'chat.sent': 'Sent • ',
        'gallery.close': 'Close gallery',
        'gallery.details': 'Details',
        'gallery.unknownTitle': 'This content can\u2019t be displayed here.',
        'gallery.unknownSub': 'Unsupported component',
        'gallery.FloorPlanShowcase': 'Floor Plans',
        'gallery.UnitCarousel': 'Available units',
        'gallery.NeighborhoodMap': 'Neighborhood',
        'gallery.BookingFrame': 'Book a visit',
        'gallery.BookingSlots': 'Book a visit',
        'gallery.BookingConfirm': 'Visit confirmed',
        'link.UnitCarousel': 'View available units',
        'link.NeighborhoodMap': 'View neighborhood',
        'link.FloorPlanShowcase': 'View floor plans',
        'link.gallery': 'View gallery',
        'map.title': 'What\u2019s nearby',
        'map.subtitle': 'Points of interest around {name}',
        'map.cat.Groceries': 'Groceries',
        'map.cat.Pharmacies': 'Pharmacies',
        'map.cat.Restaurants': 'Restaurants',
        'map.cat.Parks': 'Parks',
        'map.cat.Schools': 'Schools',
        'map.cat.Fitness': 'Gyms & Fitness',
        'map.cat.Transit': 'Transit',
        'map.cat.Shopping': 'Shopping',
        'map.cat.Other': 'Other',
        'map.loading': 'Loading map…',
        'map.error': 'Map couldn\u2019t load. Check your connection and try again.',
        'map.footnote': 'Map data © OpenStreetMap contributors. Pin locations are illustrative.',
        'map.projectLocation': 'Project location',
        'map.openInMaps': 'Open in Maps ›',
        'map.filterLabel': 'Filter points of interest by category',
        'map.mapLabel': 'Neighborhood map',
        'units.title': 'Matching units',
        'units.noPhotos': 'No photos yet',
        'units.empty': 'No matching units to display.',
        'units.book': 'Book a visit',
        'units.bed': 'bed',
        'units.beds': 'beds',
        'units.bath': 'bath',
        'units.baths': 'baths',
        'units.sqft': 'sq ft',
        'units.sqftBalcony': 'sq ft balcony',
        'units.den': '+ Den',
        'units.photosOf': '{name} photos',
        'units.photoAlt': '{name} — photo {n} of {count}',
        'units.prevPhoto': 'Previous photo',
        'units.nextPhoto': 'Next photo',
        'fp.title': 'Floor Plans',
        'fp.empty': 'No floor plans to display.',
        'fp.imageFallback': 'Floor plan',
        'fp.from': 'From'
    }
};

function normalize(lang) {
    if (typeof lang !== 'string') {
        return null;
    }
    const lower = lang.toLowerCase().slice(0, 2);
    return SUPPORTED.includes(lower) ? lower : null;
}

/** Resolve the active language: query param > stored preference > 'fr'. */
export function getLang() {
    try {
        const param = normalize(new URLSearchParams(window.location.search).get('lang'));
        if (param) {
            try {
                localStorage.setItem(LANG_KEY, param);
            } catch (e) {
                // no-op
            }
            return param;
        }
        const stored = normalize(localStorage.getItem(LANG_KEY));
        if (stored) {
            return stored;
        }
    } catch (e) {
        // no-op
    }
    return 'fr';
}

/** Persist a language preference and notify subscribers. */
export function setLang(lang) {
    const normalized = normalize(lang) || 'fr';
    try {
        localStorage.setItem(LANG_KEY, normalized);
    } catch (e) {
        // no-op
    }
    try {
        document.documentElement.lang = normalized;
        window.dispatchEvent(new CustomEvent('awac:lang-changed', { detail: { lang: normalized } }));
    } catch (e) {
        // no-op
    }
}

/** Translate a key, with optional {placeholder} substitutions. */
export function t(key, vars) {
    const lang = getLang();
    const table = STRINGS[lang] || STRINGS.fr;
    let value = table[key] != null ? table[key] : (STRINGS.en[key] != null ? STRINGS.en[key] : key);
    if (vars) {
        Object.keys(vars).forEach((name) => {
            value = value.split(`{${name}}`).join(String(vars[name]));
        });
    }
    return value;
}

/** React hook — returns [lang, setLang] and re-renders on language change. */
export function useAwacLang() {
    const [lang, setLangState] = useState(getLang);

    useEffect(() => {
        const onChange = (event) => {
            setLangState((event.detail && event.detail.lang) || getLang());
        };
        window.addEventListener('awac:lang-changed', onChange);
        return () => window.removeEventListener('awac:lang-changed', onChange);
    }, []);

    return [lang, setLang];
}
